<template>
  <div class="pay-method">
    <div class="pay-method__content">
      <!-- 支付方式切换（一次性支付且可用方式>1时显示） -->
      <Segmented
        v-if="props.buyType !== BUY_TYPE.SUBSCRIBE && data.length > 1"
        class="pay-method__methods"
        :items="tabItems"
        :model-value="String(props.payType)"
        @change="onChange"
      >
        <template #label="{ item }">{{ item.label }}</template>
      </Segmented>

      <!-- firefox 会出现滚动条 -->
      <div :class="['pay-panel', iframeClass]">
        <!-- 未勾选协议时的遮罩提示 -->
        <div v-if="!agreementChecked" class="pay-panel__check check-agreement">
          <div class="pay-panel__check-text">开通前请阅读下方协议说明</div>
          <Button
            variant="warning"
            size="compact"
            @click="onChecked(true, UNIQ_BEHAVIOR.CLICK_ACCEPT)"
          >
            同意并支付
          </Button>
        </div>
        <!-- 二维码/iframe 加载中 -->
        <div v-else-if="loading || iframeLoading" class="pay-panel__loading">
          <Loading />
        </div>
        <!-- 指定了码的大小，但是支付宝页面内元素高度多了 4px -->
        <div v-else-if="showAlipay" class="pay-panel__alipay">
          <iframe
            scrolling="no"
            :src="qrcode.alipay"
            frameborder="0"
            @loadstart="() => (iframeLoading = true)"
            @load="() => (iframeLoading = false)"
          />
        </div>
        <div v-else class="pay-panel__wechat">
          <QRCode
            :url="qrcode.weixin"
            :height="130"
            :width="130"
            :margin="1"
            class="pay-panel__wechat-qrcode"
          />
        </div>
        <!-- 二维码过期遮罩，点击可刷新重新下单 -->
        <div v-if="isTimeout" class="pay-timeout" @click="handleTimeoutClick">
          <div class="mask pay-timeout__mask">
            <div class="pay-timeout__text">{{ $t('er-wei-ma-yi-guo-qi') }}</div>
            <div class="pay-timeout__refresh">
              {{ $t('dian-ji-shua-xin') }}
              <IconRefresh class="icon-refresh" />
            </div>
          </div>
        </div>
      </div>

      <div class="pay-method__tip">{{ tipText }}</div>

      <PayPrice
        :text="priceText"
        :expiredDate="expiredDate"
        :rightName="rightName"
        class="pay-method__price"
        :showDiscountCountdown="showDiscountCountdown"
        :from="from"
      />
    </div>
    <!-- 支付协议（勾选后才可下单） -->
    <PayAgreement
      :checked="agreementChecked"
      :buyType="buyType || BUY_TYPE.PAYMENT"
      @checked="value => onChecked(value, UNIQ_BEHAVIOR.CLICK_AGREEMENT)"
    />

    <div class="pay-method__slot">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts" name="PayQrcode">
// 二维码支付容器：切换支付方式、展示二维码并处理超时刷新
import { useEnumDictionary } from '@rendora/common/composables/useEnumDictionary';
import { getBuriedService } from '@rendora/common/services/buried';
import { getI18nService } from '@rendora/common/services/i18n';
import { BUY_TYPE, PAY_TYPE } from '@rendora/common/types/enums';
import { trackUserBehavior, UNIQ_BEHAVIOR } from '@rendora/common/utils/report';
import IconRefresh from '@rendora/design/assets/refresh.svg?component';
import Button from '@rendora/design/components/button/Button.vue';
import { Loading } from '@rendora/design/components/loading/index';
import { QRCode } from '@rendora/design/components/qrcode/index';
import { Segmented } from '@rendora/design/components/segmented/index';
import { computed, ref, watch } from 'vue';
import PayAgreement from './PayAgreement.vue';
import PayPrice from './PayPrice.vue';
const i18nService = getI18nService();
const $t = i18nService.t;

// 入参定义
const props = defineProps<{
  payType: PAY_TYPE;
  buyType?: BUY_TYPE;
  agreementChecked: boolean;
  loading: boolean;
  qrcode: {
    weixin: string;
    alipay: string;
  };
  reorder?: () => void;
  priceText: number;
  expiredDate?: string;
  rightName?: string;
  showDiscountCountdown?: boolean;
  from?: string; // buy-credit
}>();
const emits = defineEmits(['update:payType', 'update:agreementChecked']);
// 内部状态
const iframeLoading = ref(false);

const isTimeout = ref(false);

let timeout: NodeJS.Timeout | null = null;

const track = getBuriedService()?.track;

// 开启二维码过期计时（2分钟）
function startTimeout() {
  isTimeout.value = false;
  timeout && clearTimeout(timeout);
  timeout = setTimeout(
    () => {
      isTimeout.value = true;
    },
    2 * 60 * 1000, // 2分钟
  );
}

watch(
  [() => props.qrcode.alipay, () => props.qrcode.weixin],
  () => {
    if (props.qrcode.alipay || props.qrcode.weixin) {
      startTimeout();
    }
  },
  {
    immediate: true,
  },
);

const PAY_TYPE_MAP = useEnumDictionary('pay_type');

const data = computed(() => {
  const buyTypes = [{ title: PAY_TYPE_MAP[PAY_TYPE.ALIPAY], value: PAY_TYPE.ALIPAY }];
  if (props.buyType !== BUY_TYPE.SUBSCRIBE) {
    buyTypes.push({
      title: PAY_TYPE_MAP[PAY_TYPE.WEIXIN],
      value: PAY_TYPE.WEIXIN,
    });
  }
  return buyTypes;
});

// tabs items derived from data
const tabItems = computed(() => data.value.map(it => ({ key: String(it.value), label: it.title })));

const isAlipay = computed(() => props.payType === PAY_TYPE.ALIPAY);

const showAlipay = computed(() => isAlipay.value && props.qrcode.alipay);

const iframeClass = computed(() => {
  return props.buyType !== BUY_TYPE.SUBSCRIBE ? 'panel-payment' : 'panel-subscribe';
});

const tipText = computed(() =>
  isAlipay.value
    ? $t('qing-shi-yong-zhi-fu-bao-sao-ma-fu-kuan')
    : $t('qing-shi-yong-wei-xin-sao-ma-fu-kuan'),
);

// 切换支付方式（通知父组件）
const onChange: InstanceType<typeof Segmented>['onChange'] = value => {
  emits('update:payType', Number(value));
};

// 勾选/取消协议
function onChecked(checked: boolean, eventName: UNIQ_BEHAVIOR) {
  emits('update:agreementChecked', checked);
  trackUserBehavior(eventName, {
    checked,
  });
}

// 二维码过期后点击刷新（重新下单）
function handleTimeoutClick() {
  isTimeout.value = false;
  props.reorder?.();
}
</script>

<style lang="scss" scoped>
.pay-method__content {
  flex: 1;
}
.pay-panel {
  display: block;
  margin: 40px auto 0;
  overflow: hidden;
  position: relative;
}
.pay-panel__check {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  height: 100%;
  background-color: var(--x-black-6);
}
.pay-panel__check-text {
  color: #fff;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  width: 98px;
  text-align: center;
  margin-bottom: 8px;
}
.icon-warning {
  color: var(--x-warning-6);
}
.pay-panel__loading {
  width: 100%;
  height: 130px;
  background-color: var(--x-black-6);
  color: var(--x-white-1);
  border-radius: var(--x-radius-medium);
  display: grid;
  place-items: center;
}
.pay-panel__alipay {
  overflow: hidden;
}
.pay-panel__wechat {
  position: absolute;
  border-radius: 6px;
}
:deep(.pay-panel__wechat-qrcode) {
  border-radius: var(--x-radius-medium);
  overflow: hidden;
}
.mask {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  padding: 8px 12px;
}
.pay-timeout {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 100%;
  background-color: var(--x-black-6);
  cursor: pointer;
  border-radius: var(--x-radius-medium);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.pay-timeout__mask {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
}
.pay-timeout__text {
  color: var(--x-text-1);
  font-size: 12px;
  line-height: 1;
}
.pay-timeout__refresh {
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--x-purple-6);
  line-height: 1;
  .icon-refresh {
    width: 16px;
    height: 16px;
  }
}

:deep(.pay-method__methods) {
  width: 100%;
}

.panel {
  pointer-events: none;
  user-select: none;
  color: var(--x-text-1);

  &-subscribe {
    position: relative;
    width: 140px;
    height: 140px;
    margin: 44px auto 8px;
    iframe {
      width: 300px;
      height: 400px;
      transform: scale(0.76);
      position: absolute;
      left: -80px;
      top: -185px;
    }
  }
  &-payment {
    width: 140px;
    border-radius: var(--x-radius-medium);
    padding: 5px;
    height: 140px;
    margin-bottom: 8px;
    background-color: var(--x-back-2);

    .check-agreement {
      min-height: 130px;
      border-radius: var(--x-radius-small);
    }

    iframe {
      height: 150px;
      transform: scale(0.8666) translate(-24px, -11px);
    }
  }
}
.pay-method {
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 270px;
  &__title {
    padding-top: 16px;
    font-size: 14px;
    line-height: 24px;
    opacity: 0.6;
  }
  &__methods {
    margin: 0px auto 16px;
    width: 216px;
  }
  &__tip {
    padding-bottom: 6px;
    font-size: 12px;
    line-height: 1;
    text-align: center;
    opacity: 0.5;
  }
  &__protocol {
    font-size: 12px;
    opacity: 0.5;
  }
  &__price {
    margin-top: 16px;
  }
  &__slot {
    margin-top: 20px;
  }
}
</style>
